import { useState } from 'react';
import { DialogTrigger, Popover } from 'react-aria-components';
import { Trans } from '@lingui/react/macro';

import { UserAvatar } from '@/components/UserAvatar/UserAvatar';
import Avatar from '@/design_system/Avatar';
import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import { useCurrentSession } from '@/services/auth';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import './UserAvatarGroup.css';

const { block, element } = createBEMClasses('user-avatar-group');

const NUMBER_AVATARS_TO_DISPLAY = 4;

export const UserAvatarGroup = ({
  users,
  size,
  withTooltip = true,
}: {
  users: { id: string; name: string }[];
  size?: 'xx-small' | 'x-small' | 'small' | 'medium';
  withTooltip?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const { currentSession } = useCurrentSession();
  const { isMobile } = useViewPort();

  // Pick the first users to display their avatars
  const firstUsers =
    users.length <= NUMBER_AVATARS_TO_DISPLAY
      ? users
      : users.slice(0, NUMBER_AVATARS_TO_DISPLAY - 1);
  const firstAvatars = (
    <FirstUserAvatars users={firstUsers} size={size} withTooltip={withTooltip} />
  );

  if (users.length <= NUMBER_AVATARS_TO_DISPLAY) {
    return <div className={block({ size })}>{firstAvatars}</div>;
  }

  const children = (
    <div className={block({ size })}>
      {firstAvatars}
      <Avatar
        size={size ?? 'medium'}
        count={users.length - NUMBER_AVATARS_TO_DISPLAY + 1}
        isButton={withTooltip}
      />
    </div>
  );

  if (!withTooltip) {
    return children;
  }

  // Then display all the others with a count and a popover
  return (
    <DialogTrigger key="count" onOpenChange={setOpen} isOpen={open} aria-label={''}>
      {children}

      <Popover placement="bottom end" offset={4}>
        <Box padding="0" className={element('others')}>
          <ul className={element('list')}>
            <>
              {users.map((user) => {
                return (
                  <li key={user.id} className={element('item')}>
                    <UserAvatar size={isMobile ? 'small' : (size ?? 'medium')} user={user} />
                    <Stack>
                      <InlineText ellipsis className="sentry-mask" flex={1} title={user.name}>
                        {user.name}
                      </InlineText>
                      {user.id === currentSession?.id && (
                        <InlineText size="200" color="disabled">
                          <Trans id="user-avatar-group.you">You</Trans>
                        </InlineText>
                      )}
                    </Stack>
                  </li>
                );
              })}
            </>
          </ul>
        </Box>
      </Popover>
    </DialogTrigger>
  );
};

const FirstUserAvatars = ({
  users,
  size,
  withTooltip = true,
}: {
  users: { id: string; name: string }[];
  size?: 'xx-small' | 'x-small' | 'small' | 'medium';
  withTooltip?: boolean;
}) => {
  return users.map((user) => (
    <UserAvatar
      key={user.id}
      user={user}
      size={size ?? 'medium'}
      tooltip={withTooltip && <p className="sentry-mask">{user.name}</p>}
    />
  ));
};
